import { useState, useEffect } from 'react';
import { useDebouncedCallback  } from 'use-debounce';
import { useNavigate } from 'react-router-dom';
import api from '../../../api/axios';
import useAuth from '../../../hooks/useAuth';
import { useLoading } from '../../../components/LoadingProvider';
import { showSuccess, showError } from '../../../utils/toast';
import { getPublicImgUrl } from '../../../utils/common';
import DateRangePicker from '@wojtekmaj/react-daterange-picker';
import '@wojtekmaj/react-daterange-picker/dist/DateRangePicker.css';
import 'react-calendar/dist/Calendar.css';

function DoctorAppointments() {
    document.title = 'Varosa - Doctor Appointments';
    const { setIsLoading } = useLoading();
    const navigate = useNavigate();
    const { user, userRole } = useAuth({ redirectPath: '/login', allowedRoles: ['doctor'] });
    const [patientSearch, setPatientSearch] = useState('');
    const debounced = useDebouncedCallback((value) => {
        setPatientSearch(value);
        fetchAppointments();
    }, 1000);
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 7);
    const [appointmentDate, setAppointmentDate] = useState([thirtyDaysAgo, today]);
    const [appointments, setAppointments] = useState({
        upcoming: [],
        ongoing: [],
        previous: []
    });

    useEffect(() => {
        // Check authentication status
        if (!user) {
            navigate('/login');
            return;
        }
        fetchAppointments();
    }, [user, navigate, appointmentDate]);
    const fetchAppointments = async () => {
        try {
            setIsLoading(true);
            let date = appointmentDate?.map((item) => item?.toLocaleDateString());
            // console.log('date', date);
            const response = await api.get(`/${userRole}/appointments`, {
                params: {
                    search_patient: patientSearch,
                    start_date: date[0],
                    end_date: date[1],
                },
            });
            if (response?.data?.result === 'success') {
                setAppointments({
                    upcoming: response?.data?.data?.upcoming_appointments || [],
                    ongoing: response?.data?.data?.ongoing_appointments || [],
                    previous: response?.data?.data?.completed_appointments || []
                });
                return;
            } else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        showError(item);
                    });
                }
            } else {
                showError('Something went wrong! Please try again later.');
            }
        } catch (error) {
            showError('Error fetching appointments:', error?.response?.data?.message || error.message);
        } finally {
            setIsLoading(false);
        }
    }

    return (
        <>
            <div className="container py-5 login-container">
                <div className="row g-3">
                    <div className="col-12">
                        <h2>My Appointments</h2>
                    </div>
                </div>
            </div>
            <div className="container-fluid feature bg-light py-5" id="findDoctor">
                <div className="container">
                    <div className="row g-4 justify-content-center">
                        <div className="col-md-12 justify-content-center align-items-center text-center col-sm-12">
                            <div className="offset-md-3 col-md-6">
                                <DateRangePicker 
                                    className="form-control py-3 border-primary bg-transparent mb-3"
                                    onChange={(e) => { setAppointmentDate(e); }} 
                                    value={appointmentDate} 
                                    format="dd/MM/yyyy"
                                />
                                <form>
                                    <div className="input-group mb-3" title="Search by Patient Name / Number">
                                        <input type="text" className="form-control py-3 border-primary bg-transparent" placeholder="Search by Patient Name / Number" onChange={(e) => {
                                            debounced(e.target.value);
                                        }} />
                                        <button type="button" className="btn btn-primary text-white py-1 px-3">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-search" viewBox="0 0 16 16">
                                                <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001q.044.06.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1 1 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0"/>
                                            </svg>
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="container pt-4">
                    <div className="section-title mb-5 wow fadeInUp" data-wow-delay="0.1s">
                        <div className="sub-style">
                            <h4 className="sub-title px-3 mb-0">Upcoming Appointments</h4>
                        </div>
                    </div>
                    <div className="row g-3">
                        {appointments.upcoming.length === 0 && (<h4 className="mb-4 text-center">No Upcoming Appointments</h4>)}
                        {appointments.upcoming.map((item, index, arr) => (
                            <div className="col-md-4 wow fadeInUp" data-wow-delay="0.1s" key={item.id}>
                                <div className="team-item rounded">
                                    <div className="team-img rounded-top h-100 border border-primary border-bottom-0 pt-3">
                                        <img src={item.patient_profile_picture || (getPublicImgUrl()+'/profile_8747415.png')} className="img-fluid rounded-top w-100" style={{ maxHeight: '250px', objectFit: 'contain' }} alt="" />
                                    </div>
                                    <div className="team-content text-center border border-primary border-top-0 rounded-bottom p-4">
                                        <h5 className="mb-2">{item.patient_name}</h5>
                                        <div className="appointment-item-div mb-3"><span><b>Date:</b></span> <span>{item.appointment_date}</span></div>
                                        <div className="appointment-item-div mb-3"><span><b>Time:</b></span> <span>{item.appointment_time}</span></div>
                                        <div className="appointment-item-div mb-3"><span><b>Reason:</b></span> <span>{item.visit_reason}</span></div>
                                        <div className="appointment-item-div mb-3"><span><b>Payment Status:</b></span> <span>{item.payment_status ?? 'N/A'}</span></div>
                                        <div className="appointment-item-div mb-3"><span><b>Booking Status:</b></span> <span>{item.booking_status ?? 'N/A'}</span></div>
                                        <div className="appointment-item-div mb-3"><span><b>Fees:</b></span> <span><b>&#8377;{item.total_amount}</b></span></div>
                                        <hr />
                                        <button type="button" onClick={() => navigate(`/dashboard/doctor/appointment/${item.id}`)} className="btn btn-primary">View Details</button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="container pt-4">
                    <div className="section-title mb-5 wow fadeInUp" data-wow-delay="0.1s">
                        <div className="sub-style">
                            <h4 className="sub-title px-3 mb-0">Ongoing Appointments</h4>
                        </div>
                    </div>
                    <div className="row g-3 justify-content-center">
                        {appointments.ongoing.length === 0 && (<h4 className="mb-4 text-center">No Ongoing Appointments</h4>)}
                        {appointments.ongoing.map((item, index, arr) => (
                            <div className="col-md-4 wow fadeInUp" data-wow-delay="0.1s" key={item.id}>
                                <div className="team-item rounded">
                                    <div className="team-img rounded-top h-100 border border-primary border-bottom-0 pt-3">
                                        <img src={item.patient_profile_picture || (getPublicImgUrl()+'/profile_8747415.png')} className="img-fluid rounded-top w-100" style={{ maxHeight: '250px', objectFit: 'contain' }} alt="" />
                                    </div>
                                    <div className="team-content text-center border border-primary border-top-0 rounded-bottom p-4">
                                        <h5 className="mb-2">{item.patient_name}</h5>
                                        <div className="appointment-item-div mb-3"><span><b>Date:</b></span> <span>{item.appointment_date}</span></div>
                                        <div className="appointment-item-div mb-3"><span><b>Time:</b></span> <span>{item.appointment_time}</span></div>
                                        <div className="appointment-item-div mb-3"><span><b>Reason:</b></span> <span>{item.visit_reason}</span></div>
                                        <div className="appointment-item-div mb-3"><span><b>Payment Status:</b></span> <span>{item.payment_status ?? 'N/A'}</span></div>
                                        <div className="appointment-item-div mb-3"><span><b>Booking Status:</b></span> <span>{item.booking_status ?? 'N/A'}</span></div>
                                        <div className="appointment-item-div mb-3"><span><b>Fees:</b></span> <span><b>&#8377;{item.total_amount}</b></span></div>
                                        <hr />
                                        <button type="button" onClick={() => navigate(`/dashboard/doctor/appointment/${item.id}`)} className="btn btn-primary">View Details</button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="container pt-4">
                    <div className="section-title mb-5 wow fadeInUp" data-wow-delay="0.1s">
                        <div className="sub-style">
                            <h4 className="sub-title px-3 mb-0">Previous Appointments</h4>
                        </div>
                    </div>
                    <div className="row g-3 justify-content-center">
                        {appointments.previous.length === 0 && (<h4 className="mb-4 text-center">No Previous Appointments</h4>)}
                        {appointments.previous.map((item, index, arr) => (
                            <div className="col-md-4 wow fadeInUp" data-wow-delay="0.1s" key={item.id}>
                                <div className="team-item rounded">
                                    <div className="team-img rounded-top h-100 border border-primary border-bottom-0 pt-3">
                                        <img src={item.patient_profile_picture || (getPublicImgUrl()+'/profile_8747415.png')} className="img-fluid rounded-top w-100" style={{ maxHeight: '250px', objectFit: 'contain' }} alt="" />
                                    </div>
                                    <div className="team-content text-center border border-primary border-top-0 rounded-bottom p-4">
                                        <h5 className="mb-2">{item.patient_name}</h5>
                                        <div className="appointment-item-div mb-3"><span><b>Date:</b></span> <span>{item.appointment_date}</span></div>
                                        <div className="appointment-item-div mb-3"><span><b>Time:</b></span> <span>{item.appointment_time}</span></div>
                                        <div className="appointment-item-div mb-3"><span><b>Reason:</b></span> <span>{item.visit_reason}</span></div>
                                        <div className="appointment-item-div mb-3"><span><b>Payment Status:</b></span> <span>{item.payment_status ?? 'N/A'}</span></div>
                                        <div className="appointment-item-div mb-3"><span><b>Booking Status:</b></span> <span>{item.booking_status ?? 'N/A'}</span></div>
                                        <div className="appointment-item-div mb-3"><span><b>Fees:</b></span> <span><b>&#8377;{item.total_amount}</b></span></div>
                                        <hr />
                                        <button type="button" onClick={() => navigate(`/dashboard/doctor/appointment/${item.id}`)} className="btn btn-primary">View Details</button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </>
    );
}

export default DoctorAppointments;